import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { LoadingService } from '@src/app/core/services/loading/loading.service';
import { SessionRefreshService } from '@src/app/core/auth/session-refresh.service';
import { AuthActions } from '@src/app/store/auth/auth.actions';
import { AuthSelectors } from '@src/app/store/auth/auth.selectors';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { catchError, finalize, of, switchMap, take, withLatestFrom } from 'rxjs';

export const authGuard: CanActivateFn = (route, state) => {
  const loadingService = inject(LoadingService);
  const router = inject(Router);
  const store = inject(Store);
  const oidcSecurityService = inject(OidcSecurityService);
  const sessionRefreshService = inject(SessionRefreshService);
  const actions$ = inject(Actions);

  // Mostrar loading
  loadingService.show();
  // loadingService.hide();
  // return of(true);

  // Simplify and compose the observable chain so every path emits a boolean,
  // then subscribe once and resolve the Promise accordingly.
  return new Promise((resolve) => {
    oidcSecurityService
      .getAccessToken()
      .pipe(
        take(1),
        switchMap((token) => {
          if (!token) {
            sessionStorage.setItem('redirectUrl', state.url);
            router.navigate(['login']);
            loadingService.hide();
            return of(false);
          }

          return oidcSecurityService.checkAuthIncludingServer().pipe(
            take(1),
            switchMap((authenticated) => {
              if (!authenticated?.isAuthenticated) {
                // PBI-866415 / PBI-877361: antes de mandar al usuario a /login,
                // renovamos con el refresh_token (renovación compartida con el
                // interceptor y los eventos de la librería). Si vuelve OK, el
                // usuario sigue navegando como si nada. Si falla, entonces sí → /login.
                return sessionRefreshService
                  .refresh('guard')
                  .pipe(
                    switchMap((renewed) =>
                      renewed
                        ? handleAuthenticatedFlow()
                        : handleUnauthenticated(),
                    ),
                    catchError((err) => {
                      console.error('[AUTH] Error renovando la sesión en authGuard', err);
                      return handleUnauthenticated();
                    }),
                  );
              }

              return handleAuthenticatedFlow();
            }),
          );

          function handleUnauthenticated() {
            // Anti-loop: si acabamos de volver de authorize() sin sesión válida,
            // no reintentamos infinitamente contra el IDP; caemos a /login.
            const AUTO_AUTHORIZE_KEY = 'auth:autoAuthorizeAt';
            const LOOP_WINDOW_MS = 30_000;
            return oidcSecurityService.getPayloadFromAccessToken().pipe(
              take(1),
              switchMap((payload) => {
                const exp = payload?.exp;
                const currentTime = Math.floor(Date.now() / 1000);
                const expired = !!(exp && exp < currentTime);
                const lastAttempt = Number(
                  sessionStorage.getItem(AUTO_AUTHORIZE_KEY) ?? 0,
                );
                const now = Date.now();
                const withinLoopWindow = now - lastAttempt < LOOP_WINDOW_MS;

                sessionStorage.setItem('redirectUrl', state.url);

                if (withinLoopWindow) {
                  // El auto-authorize acaba de fallar (no hay SSO en el CIAM
                  // o el IDP rechazó): mostramos la pantalla de login normal
                  // con el flag de sesión expirada para que el usuario
                  // reautentique manualmente.
                  sessionStorage.removeItem(AUTO_AUTHORIZE_KEY);
                  if (expired) {
                    localStorage.setItem('sessionExpired', 'true');
                  }
                  router.navigate(['login']);
                  loadingService.hide();
                  return of(false);
                }

                // Primer intento: pedimos un round-trip al CIAM. Si el usuario
                // ya tiene sesión SSO válida allí, volverá al /callback con un
                // code y la sesión se restaurará sin interacción. Si no, el
                // CIAM mostrará su pantalla de login.
                sessionStorage.setItem(AUTO_AUTHORIZE_KEY, String(now));
                try {
                  oidcSecurityService.authorize();
                } catch (err) {
                  console.error('[AUTH] Error redirigiendo al login del IDP', err);
                  sessionStorage.removeItem(AUTO_AUTHORIZE_KEY);
                  if (expired) {
                    localStorage.setItem('sessionExpired', 'true');
                  }
                  router.navigate(['login']);
                }
                loadingService.hide();
                return of(false);
              }),
            );
          }

          function handleAuthenticatedFlow() {
              // Cache: si el usuario ya está cargado en el store, evita re-despachar
              // loadUserInfo. Esto rompe el loop en navegaciones sucesivas (p.ej. el
              // refresh trick de validateKamAccessOnCompanyChange$, que navega
              // /help → /home dentro del mismo layout) — sin esta comprobación, cada
              // navegación despachaba loadUserInfo y el switchMap del loadUserInfo$
              // effect cancelaba la petición HTTP getCurrentUsuario en curso.
              return store
                .select(AuthSelectors.selectAuthUser)
                .pipe(
                  take(1),
                  withLatestFrom(
                    store.select(AuthSelectors.canAccessPortal),
                    store.select(AuthSelectors.selectCompanies),
                  ),
                  switchMap(([cachedUser, cachedCanAccess, cachedCompanies]) => {
                    if (
                      cachedUser?.NOMBRE_DS &&
                      cachedCanAccess &&
                      cachedCompanies.length > 0
                    ) {
                      resolve(true);
                      loadingService.hide();
                      return of(true);
                    }

                    store.dispatch(AuthActions.loadUserInfo());
                    return actions$.pipe(
                      ofType(
                        AuthActions.loadUserInfoSuccess,
                        AuthActions.loadUserInfoFailure,
                      ),
                      withLatestFrom(
                        store.select(AuthSelectors.selectAuthUser),
                        store.select(AuthSelectors.canAccessPortal),
                        store.select(AuthSelectors.selectCompanies),
                      ),
                      take(1),
                      switchMap(([, user, canAccessPortal, companies]) => {
                        // Una vez terminadas las cargas, verificar si tenemos usuario y token
                        if (
                          user?.NOMBRE_DS &&
                          canAccessPortal &&
                          companies.length > 0
                        ) {
                          resolve(true);
                          loadingService.hide();
                          return of(true);
                        } else {
                          store.dispatch(
                            AuthActions.setShowErrorLogin({ show: true }),
                          );
                          sessionStorage.setItem('redirectUrl', state.url);
                          router.navigate(['login']);
                          loadingService.hide();
                          resolve(false);
                          return of(false);
                        }
                      }),
                    );
                  }),
                );
          }
        }),
      )
      .pipe(
        finalize(() => {
          loadingService.hide();
        }),
      )
      .subscribe({
        next: (canActivate: boolean) => {
          resolve(canActivate);
        },
        error: (error) => {
          console.error('[AUTH] Error en authGuard', error);
          router.navigate(['login']);
          loadingService.hide();
          resolve(false);
        },
      });
  });
};

